/* ==========================================================================
   Sanctus Servo — Immersive Layer engine ("Awwwards mode")
   --------------------------------------------------------------------------
   Vanilla JS, no libraries. Reads window.SanctusImmersive (set by
   snippets/sanctus-immersive-head.liquid from theme settings) and powers:
     1. Kinetic hero  — splits slideshow headings into masked word spans
     2. Parallax + golden scroll thread (one shared rAF scroll loop)
     3. Entrance veil cleanup
   Scroll reveals need no JS here — Dawn's own scroll-trigger system runs
   them; sanctus-immersive.css only upgrades the animation variables.
   Everything no-ops under prefers-reduced-motion. Safe by construction:
   every effect's resting state is "fully visible".
   ========================================================================== */

(function () {
  'use strict';

  var cfg = window.SanctusImmersive;
  if (!cfg || !cfg.enabled) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var desktopMq = window.matchMedia('(min-width: 990px)');

  /* ------------------------------------------------------------------ */
  /* 1. KINETIC HERO — word splitting                                    */
  /* ------------------------------------------------------------------ */
  /* Wraps each word of the slideshow headings in a masked span pair,
     preserving any inline markup (<strong> etc.). Word-level spans stay
     screen-reader friendly. CSS keys the rise off the slide's
     aria-hidden state, so it replays on every slide change. */

  function splitHeading(heading) {
    if (heading.classList.contains('sanctus-split')) return;
    var wordIndex = 0;

    function wrapTextNode(textNode) {
      var frag = document.createDocumentFragment();
      textNode.textContent.split(/(\s+)/).forEach(function (part) {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part));
          return;
        }
        var mask = document.createElement('span');
        mask.className = 'sanctus-w';
        var inner = document.createElement('span');
        inner.className = 'sanctus-w__i';
        inner.textContent = part;
        inner.style.setProperty('--wi', wordIndex++);
        mask.appendChild(inner);
        frag.appendChild(mask);
      });
      textNode.parentNode.replaceChild(frag, textNode);
    }

    function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (child) {
        if (child.nodeType === Node.TEXT_NODE && child.textContent.trim()) {
          wrapTextNode(child);
        } else if (child.nodeType === Node.ELEMENT_NODE && !child.classList.contains('sanctus-w')) {
          walk(child);
        }
      });
    }

    walk(heading);
    heading.classList.add('sanctus-split');
  }

  function splitAllHeroHeadings() {
    document.querySelectorAll('.slideshow .banner__heading').forEach(splitHeading);
  }

  if (cfg.hero) {
    splitAllHeroHeadings();
    // Theme editor: re-split after a section is re-rendered.
    document.addEventListener('shopify:section:load', splitAllHeroHeadings);

    // The hero sequence waits for html.sanctus-hero-go. With the entrance
    // veil up, that's the moment the curtain starts lifting — so the words
    // rise into view as the page is revealed instead of finishing unseen
    // behind the veil. Without a veil, it fires immediately.
    var heroArmed = false;
    var startHero = function () {
      if (heroArmed) return;
      heroArmed = true;
      document.documentElement.classList.add('sanctus-hero-go');
    };
    var veilUp = document.documentElement.classList.contains('sanctus-veil-on');
    var veilForHero = veilUp && document.querySelector('.sanctus-veil');
    if (veilForHero) {
      veilForHero.addEventListener('animationstart', function (e) {
        if (e.target === veilForHero && e.animationName === 'sanctus-veil-lift') startHero();
      });
      // Failsafe: if the lift never fires (CSS blocked etc.), start anyway.
      setTimeout(startHero, 2600);
    } else {
      startHero();
    }
  }

  /* ------------------------------------------------------------------ */
  /* 2. PARALLAX + GOLDEN THREAD (shared rAF scroll loop)                */
  /* ------------------------------------------------------------------ */

  var parallaxTargets = [];

  function collectParallaxTargets() {
    parallaxTargets = [];
    if (!cfg.parallax || !desktopMq.matches) return;
    // Hero slides, image banners, and the image-with-text rows.
    document.querySelectorAll('.banner__media, .image-with-text__media').forEach(function (el) {
      if (!el.querySelector('img')) return;
      el.classList.add('sanctus-parallax-media');
      parallaxTargets.push(el);
    });
  }

  var ticking = false;

  function onScrollFrame() {
    ticking = false;
    var vh = window.innerHeight;

    if (cfg.progress) {
      var max = document.documentElement.scrollHeight - vh;
      var progress = max > 0 ? window.scrollY / max : 0;
      document.documentElement.style.setProperty('--sanctus-progress', Math.min(1, Math.max(0, progress)).toFixed(4));
    }

    for (var i = 0; i < parallaxTargets.length; i++) {
      var el = parallaxTargets[i];
      var rect = el.getBoundingClientRect();
      if (rect.bottom < -120 || rect.top > vh + 120) continue;
      // -1..1 as the element's center crosses the viewport center.
      var p = (rect.top + rect.height / 2 - vh / 2) / vh;
      // Never ask for more travel than the 1.12 scale bleed provides.
      var depth = Math.min(26, rect.height * 0.05);
      el.style.setProperty('--spy', (p * depth).toFixed(1) + 'px');
    }
  }

  function requestScrollFrame() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(onScrollFrame);
    }
  }

  if (cfg.parallax || cfg.progress) {
    collectParallaxTargets();
    window.addEventListener('scroll', requestScrollFrame, { passive: true });
    window.addEventListener('resize', requestScrollFrame);
    if (desktopMq.addEventListener) {
      desktopMq.addEventListener('change', function () {
        collectParallaxTargets();
        requestScrollFrame();
      });
    }
    document.addEventListener('shopify:section:load', function () {
      collectParallaxTargets();
      requestScrollFrame();
    });
    requestScrollFrame();
  }

  /* ------------------------------------------------------------------ */
  /* 3. ENTRANCE VEIL CLEANUP                                            */
  /* ------------------------------------------------------------------ */
  /* The veil's whole timeline is CSS; once the lift finishes we remove
     it from the layout entirely. */

  var veil = document.querySelector('.sanctus-veil');
  if (veil) {
    veil.addEventListener('animationend', function (e) {
      if (e.target === veil && e.animationName === 'sanctus-veil-lift') {
        veil.style.display = 'none';
      }
    });
  }
})();
